import { api } from './api';

export interface AutocompleteSuggestion {
  placeId: string;
  description: string;
  mainText: string;
  secondaryText: string;
}

export interface PlaceDetailsResult {
  street: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  latitude: number;
  longitude: number;
  formattedAddress: string;
}

/**
 * Generate a unique session token for autocomplete session billing.
 * A session groups an autocomplete search + place details lookup.
 */
function generateSessionToken(): string {
  return crypto.randomUUID();
}

class AddressAutocompleteService {
  private sessionToken: string = generateSessionToken();

  /**
   * Start a new autocomplete session (generates new session token)
   */
  newSession(): void {
    this.sessionToken = generateSessionToken();
  }

  /**
   * Get autocomplete suggestions for address input
   */
  async getSuggestions(input: string): Promise<AutocompleteSuggestion[]> {
    if (!input || input.trim().length < 3) return [];

    try {
      const response = await api.post('/address/autocomplete', {
        input: input.trim(),
        sessionToken: this.sessionToken,
      });
      return response.data.suggestions || [];
    } catch {
      return [];
    }
  }

  /**
   * Get place details for a selected suggestion.
   * Completes the session and generates a new token.
   */
  async getPlaceDetails(placeId: string): Promise<PlaceDetailsResult | null> {
    try {
      const response = await api.post('/address/place-details', {
        placeId,
        sessionToken: this.sessionToken,
      });
      // Session complete — start a new one for next search
      this.newSession();
      return response.data.place || null;
    } catch {
      return null;
    }
  }
}

export const addressAutocompleteService = new AddressAutocompleteService();
